import React, { ButtonHTMLAttributes, ReactNode, FC } from "react";
import { MoreVertical, LucideIcon, ChevronRight } from "lucide-react";

// ------------------------------
// INTERFACE'LER (Prop Tiplendirmeleri)
// ------------------------------

// HTML'deki standart <button> elemanının tüm özelliklerini içerir
interface BaseButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  className?: string;
  // onClick ve type zaten ButtonHTMLAttributes içinde var, ancak netlik için burada tutulabilir
  // onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  // type?: "button" | "submit" | "reset";
}

interface ThemedButtonProps extends BaseButtonProps {}

interface IconButtonProps extends Omit<BaseButtonProps, 'children'> {
  icon: LucideIcon;
  tooltip?: string; // title yerine daha modern bir isim
}

interface LoadingButtonProps extends ThemedButtonProps {
  loading: boolean;
}

interface IconTextButtonProps extends ThemedButtonProps {
  icon: LucideIcon;
  loading?: boolean;   // Eklendi
}

// ------------------------------
// BASE BUTTON
// ------------------------------
// BaseButtonProps artık onClick ve type dahil tüm <button> özelliklerini kabul eder
export const Button: FC<BaseButtonProps> = ({ children, className = "", onClick, type = "button", ...rest }) => {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`btn ${className}`}
      {...rest} // Diğer standart HTML buton özelliklerini (disabled, form vb.) iletmek için
    >
      {children}
    </button>
  );
};

// ------------------------------
// PRIMARY BUTTON
// ------------------------------
export const PrimaryButton: FC<ThemedButtonProps> = ({ children, className = "", ...rest }) => {
  return (
    <Button className={`btn-primary ${className}`} {...rest}>
      {children}
    </Button>
  );
};

// ------------------------------
// SECONDARY BUTTON
// ------------------------------
export const SecondaryButton: FC<ThemedButtonProps> = ({ children, className = "", ...rest }) => {
  return (
    <Button className={`btn-secondary ${className}`} {...rest}>
      {children}
    </Button>
  );
};

// ------------------------------
// DANGER BUTTON
// ------------------------------
export const DangerButton: FC<ThemedButtonProps> = ({ children, className = "", ...rest }) => {
  return (
    <Button className={`btn-danger ${className}`} {...rest}>
      {children}
    </Button>
  );
};

// ------------------------------
// TEXT BUTTON (Themed)
// ------------------------------
export const TextButton: FC<ThemedButtonProps> = ({ children, className = "", ...rest }) => {
  return (
    <button
      className={`font-medium transition px-2 py-1 rounded hover:opacity-80 text-[var(--text)] ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

// ------------------------------
// ICON BUTTON (Themed)
// ------------------------------
export const IconButton: FC<IconButtonProps> = ({ icon: Icon, onClick, className = "", tooltip, ...rest }) => {
  return (
    <button
      onClick={onClick}
      // "p-custom-tooltip" sınıfını biz uydurduk, PrimeReact bu sınıfa sahip butonları yakalayacak
      className={`p-2 rounded-full transition shadow hover:opacity-80 bg-[var(--card)] text-[var(--text)] p-custom-tooltip disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none disabled:shadow-none ${className}`}
      data-pr-tooltip={tooltip}
      data-pr-position="top" // Üstte açılması için
      {...rest}
    >
      <Icon size={18} />
    </button>
  );
};

// ------------------------------
// MENU BUTTON (3 DOT / Vertical Dots)
// ------------------------------
interface MenuButtonProps {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  badge?: string | number;
  className?: string;
}

export const MenuButton: FC<MenuButtonProps> = ({ icon, label, onClick, badge, className = "" }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-3 rounded-xl hover:bg-[var(--bg-alt)] transition-all duration-200 group ${className}`}
    >
      <div className="flex items-center gap-3">
        {/* İkon Kutusu */}
        <div className="text-[var(--text)] opacity-60 group-hover:opacity-100 group-hover:text-[var(--primary)] transition-all">
          {icon}
        </div>
        
        {/* Metin */}
        <span className="font-bold text-sm text-[var(--text)] opacity-80 group-hover:opacity-100">
          {label}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {/* Bildirim Rozeti */}
        {badge && (
          <span className="bg-[var(--danger)] text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-lg shadow-[var(--danger)]/20">
            {badge}
          </span>
        )}
        
        {/* Sağ Ok */}
        <ChevronRight size={14} className="text-[var(--text)] opacity-20 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
      </div>
    </button>
  );
};

// ------------------------------
// LOADING BUTTON
// ------------------------------
export const LoadingButton: FC<LoadingButtonProps> = ({ loading, children, className = "", onClick }) => {
  return (
    <Button
      // loading durumunda onClick'i devre dışı bırakıyoruz
      onClick={!loading ? onClick : undefined}
      className={`btn-primary flex items-center justify-center gap-2 ${loading ? "opacity-70 cursor-not-allowed" : ""} ${className}`}
      disabled={loading} // Butonu tam olarak devre dışı bırakmak için
    >
      {loading && (
        // Animasyon için Tailwind'i kullanıyoruz
        <span className="w-4 h-4 border-2 border-t-transparent border-[var(--btn-text)] rounded-full animate-spin" />
      )}
      {children}
    </Button>
  );
};

// ------------------------------
// ICON + TEXT BUTTON
// ------------------------------
export const IconTextButton: FC<IconTextButtonProps> = ({ 
  icon: Icon, 
  children, 
  onClick, 
  className = "",
  loading,
  disabled
}) => {
  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      className={`flex items-center gap-2 ${loading ? "opacity-70 cursor-not-allowed" : ""} ${className}`}
    >
      {loading && (
        <span className="w-4 h-4 border-2 border-t-transparent border-[var(--btn-text)] rounded-full animate-spin" />
      )}
      {!loading && <Icon size={18} />}
      {children}
    </Button>
  );
};